import React, { useState, useRef, useEffect } from 'react';
import './WebMusicPlayer.css';

interface Track {
  id: number;
  title: string;
  duration: string;
  audioUrl: string;
}

interface MediaItem {
  type: 'text' | 'image';
  content: string;
  caption?: string;
}

interface AlbumContent {
  title: string;
  artist: string;
  artwork: string;
  tracks: Track[];
  media?: MediaItem[];
  artistBio?: string;
  artistPhoto?: string;
  artistInstagram?: string;
  artistWebsite?: string;
}

interface WebMusicPlayerProps {
  content: AlbumContent;
  expiresAt: string | null;
  onBack: () => void;
}

type Tab = 'music' | 'notes' | 'artist';

function WebMusicPlayer({ content, expiresAt, onBack }: WebMusicPlayerProps) {
  const [currentTab, setCurrentTab] = useState<Tab>('music');
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [prevVolume, setPrevVolume] = useState(1);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Determine available tabs
  const hasMusic = content.tracks && content.tracks.length > 0;
  const hasNotes = content.media && content.media.length > 0;
  const hasArtist = content.artistBio || content.artistPhoto || content.artistInstagram || content.artistWebsite;

  // Set initial tab and auto-select first track
  useEffect(() => {
    if (hasMusic) {
      setCurrentTab('music');
      // Auto-select first track
      if (content.tracks.length > 0) {
        setCurrentTrack(content.tracks[0]);
      }
    } else if (hasNotes) {
      setCurrentTab('notes');
    } else if (hasArtist) {
      setCurrentTab('artist');
    }
  }, [hasMusic, hasNotes, hasArtist, content.tracks]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      // Auto-play next track
      const currentIndex = content.tracks.findIndex(t => t.id === currentTrack?.id);
      if (currentIndex >= 0 && currentIndex < content.tracks.length - 1) {
        const nextTrack = content.tracks[currentIndex + 1];
        setCurrentTrack(nextTrack);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack, content.tracks]);

  // Auto-play when track changes
  useEffect(() => {
    if (currentTrack && audioRef.current) {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.error('Playback failed:', err);
        setIsPlaying(false);
      });
    }
  }, [currentTrack]);

  const handlePlayPause = (track: Track) => {
    if (currentTrack?.id === track.id) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      setCurrentTrack(track);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume > 0) {
      setPrevVolume(newVolume);
    }
  };

  const toggleMute = () => {
    if (isMuted || volume === 0) {
      // Unmute: restore previous volume
      const restoreVolume = prevVolume > 0 ? prevVolume : 1;
      setVolume(restoreVolume);
      setIsMuted(false);
      if (audioRef.current) {
        audioRef.current.volume = restoreVolume;
      }
    } else {
      // Mute: save current volume and set to 0
      setPrevVolume(volume);
      setVolume(0);
      setIsMuted(true);
      if (audioRef.current) {
        audioRef.current.volume = 0;
      }
    }
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderPlayingIndicator = () => (
    <div className="playing-indicator">
      <div className="bar"></div>
      <div className="bar"></div>
      <div className="bar"></div>
    </div>
  );

  const renderMusicTab = () => (
    <div className="music-tab">
      <div className="track-list">
        {content.tracks.map((track) => {
          const isCurrentTrack = currentTrack?.id === track.id;
          const isCurrentlyPlaying = isCurrentTrack && isPlaying;

          return (
            <div
              key={track.id}
              className={`track-item ${isCurrentTrack ? 'active' : ''}`}
              onClick={() => handlePlayPause(track)}
            >
              <div className="track-play-button">
                {isCurrentlyPlaying ? (
                  renderPlayingIndicator()
                ) : (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M2 1.5v9l8-4.5-8-4.5z" />
                  </svg>
                )}
              </div>
              <div className="track-info">
                <div className="track-title">{track.title}</div>
                <div className="track-duration">{track.duration}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="notes-tab">
      {content.media?.map((item, index) => (
        <div key={index} className="media-item">
          {item.type === 'text' ? (
            <p className="media-text">{item.content}</p>
          ) : (
            <div className="media-image-container">
              <img src={item.content} alt={item.caption || 'Album media'} className="media-image" />
              {item.caption && <p className="media-caption">{item.caption}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderArtistTab = () => (
    <div className="artist-tab">
      {content.artistPhoto && (
        <div className="artist-photo-container">
          <img src={content.artistPhoto} alt={content.artist} className="artist-photo" />
        </div>
      )}
      {content.artistBio && (
        <div className="artist-bio">
          <p>{content.artistBio}</p>
        </div>
      )}
      {(content.artistInstagram || content.artistWebsite) && (
        <div className="artist-links">
          {content.artistInstagram && (
            <a href={content.artistInstagram} target="_blank" rel="noopener noreferrer" className="artist-link">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Instagram
            </a>
          )}
          {content.artistWebsite && (
            <a href={content.artistWebsite} target="_blank" rel="noopener noreferrer" className="artist-link">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              Website
            </a>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="web-music-player">
      {/* Header */}
      <div className="player-header">
        <button className="back-button" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
        <div className="header-logo">album.cards</div>
      </div>

      {/* Main Content */}
      <div className="player-content">
        {/* Left Column - Album Info */}
        <div className="album-info">
          <div className="album-artwork-container">
            <img src={content.artwork} alt={content.title} className="album-artwork" />
          </div>
          <h1 className="album-title">{content.title}</h1>
          <h2 className="album-artist">{content.artist}</h2>
          {expiresAt && (
            <p className="expiry-notice">
              Access expires: {new Date(expiresAt).toLocaleString()}
            </p>
          )}
        </div>

        {/* Right Column - Tab Content */}
        <div className="tab-container">
          {/* Tab Bar */}
          <div className="tab-bar">
            {hasMusic && (
              <button
                className={`tab-button ${currentTab === 'music' ? 'active' : ''}`}
                onClick={() => setCurrentTab('music')}
              >
                Music
              </button>
            )}
            {hasNotes && (
              <button
                className={`tab-button ${currentTab === 'notes' ? 'active' : ''}`}
                onClick={() => setCurrentTab('notes')}
              >
                Notes
              </button>
            )}
            {hasArtist && (
              <button
                className={`tab-button ${currentTab === 'artist' ? 'active' : ''}`}
                onClick={() => setCurrentTab('artist')}
              >
                Artist
              </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {currentTab === 'music' && hasMusic && renderMusicTab()}
            {currentTab === 'notes' && hasNotes && renderNotesTab()}
            {currentTab === 'artist' && hasArtist && renderArtistTab()}
          </div>
        </div>
      </div>

      {/* Bottom Player Controls - Always visible */}
      <div className="bottom-player">
        <div className="bottom-player-content">
          {/* Left: Track Info */}
          <div className="bottom-player-info">
            {currentTrack ? (
              <>
                <div className="bottom-track-title">{currentTrack.title}</div>
                <div className="bottom-track-artist">{content.artist}</div>
              </>
            ) : (
              <div className="bottom-track-title idle">Select a track to play</div>
            )}
          </div>

          {/* Center: Seek bar + Controls */}
          <div className="bottom-player-center">
            <div className="bottom-player-progress">
              <span className="time-display">{formatTime(currentTime)}</span>
              <input
                type="range"
                min="0"
                max={duration || 100}
                value={currentTime}
                onChange={handleSeek}
                className="progress-bar"
                disabled={!currentTrack}
              />
              <span className="time-display">{formatTime(duration)}</span>
            </div>

            <div className="bottom-player-controls">
              <button
                className="control-button skip-button"
                onClick={() => {
                  if (!currentTrack) return;
                  const currentIndex = content.tracks.findIndex(t => t.id === currentTrack.id);
                  if (currentIndex > 0) {
                    setCurrentTrack(content.tracks[currentIndex - 1]);
                  }
                }}
                disabled={!currentTrack || content.tracks.findIndex(t => t.id === currentTrack?.id) === 0}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                </svg>
              </button>

              <button
                className="control-button play-pause-button"
                onClick={() => {
                  if (currentTrack) {
                    handlePlayPause(currentTrack);
                  } else if (content.tracks.length > 0) {
                    setCurrentTrack(content.tracks[0]);
                  }
                }}
              >
                {isPlaying ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4h4v16H6zM14 4h4v16h-4z"/>
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>

              <button
                className="control-button skip-button"
                onClick={() => {
                  if (!currentTrack) return;
                  const currentIndex = content.tracks.findIndex(t => t.id === currentTrack.id);
                  if (currentIndex < content.tracks.length - 1) {
                    setCurrentTrack(content.tracks[currentIndex + 1]);
                  }
                }}
                disabled={!currentTrack || content.tracks.findIndex(t => t.id === currentTrack?.id) === content.tracks.length - 1}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 18l8.5-6L6 6zm10-12v12h2V6z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Right: Volume Controls */}
          <div className="bottom-player-volume">
            <button className="volume-button" onClick={toggleMute}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {volume === 0 ? (
                    <path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6"/>
                  ) : volume < 0.5 ? (
                    <path d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 0 1 0 7.07"/>
                  ) : (
                    <path d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14"/>
                  )}
                </svg>
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider"
            />
          </div>
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={currentTrack?.audioUrl}
      />
    </div>
  );
}

export default WebMusicPlayer;
